import { HttpClient, HttpEvent, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { enpointCervinoWeb } from 'src/app/configuration/enpointsCervino';
import { PreInvoicingAlertModel } from 'src/app/models/invoicing/pre-invoicing-alert.models';
import { CompositeArticleDetailModel } from 'src/app/models/preliminaries/composite-article-detail.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ArticleSelectionTableModel } from 'src/app/models/tables/archive/article-selection-table.model';
import { CompositeArticleDetailCaptureTableModel } from 'src/app/models/tables/preliminaries/composite-article-detail-capture-table.model';
import { CompositeArticleDetailSelectTableModel } from 'src/app/models/tables/preliminaries/composite-article-select-detail-table.model';
import { environment } from 'src/environments/environment';

@Injectable({
    providedIn: 'root'
})
export class CompositeArticleDetailService {

    private readonly _httpClient = inject(HttpClient);

    create(compositeArticleDetailContract: CompositeArticleDetailModel): Observable<CompositeArticleDetailModel> {
        return this._httpClient.post<CompositeArticleDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.CreateAsync}`,
            compositeArticleDetailContract
        );
    }

    createDetails(compositeArticleDetails: Array<CompositeArticleDetailModel>): Observable<boolean> {
        return this._httpClient.post<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.CreateDetailsAsync}`,
            compositeArticleDetails
        );
    }

    delete(compositeArticleDetailId: number): Observable<CompositeArticleDetailModel> {
        const params = new HttpParams().set('compositeArticleDetailId', compositeArticleDetailId);
        return this._httpClient.delete<CompositeArticleDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.DeleteAsync}`,
            { params: params }
        );
    }

    deleteSomeDetails(compositeArticleDetailIds: Array<number>): Observable<boolean> {
        return this._httpClient.put<boolean>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.DeleteSomeDetailsAsync}`,
            compositeArticleDetailIds
        );
    }

    downloadGuideImport(): Observable<HttpEvent<Blob>> {
        return this._httpClient.request(new HttpRequest('GET', `${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.DownloadGuideImport}`,
            null, { reportProgress: true, responseType: 'blob' })
        );
    }

    exportDetailConsultation(filters: ColumnResponseModel[], columnSort: string | null, ascendant: boolean): Observable<Blob> {
        const params = new HttpParams().set('columnSort', columnSort ?? '').set('ascendant', ascendant.toString());
        return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.ExportDetailConsultation}`,
            filters, { params, responseType: 'blob' }
        );
    }

    exportImportErrors(alerts: PreInvoicingAlertModel[]): Observable<Blob> {
        return this._httpClient.post(`${environment.apiCervinoBackend}${enpointCervinoWeb.masterSolutionDetail.ExportImportErrors}`, alerts,
            { responseType: 'blob' }
        );
    }

    getArticlesForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<ArticleSelectionTableModel>> {
        const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
        return this._httpClient.post<PagedResponseModel<ArticleSelectionTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetArticlesForSelectionAsync}`,
            filters, { params: params }
        );
    }

    getCompositeArticleDetailById(compositeArticleDetailId: number): Observable<CompositeArticleDetailModel> {
        const params = new HttpParams().set('compositeArticleDetailId', compositeArticleDetailId);
        return this._httpClient.get<CompositeArticleDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetByIdAsync}`,
            { params: params }
        );
    }

    getColumnsForArticleSelection(): Observable<ColumnResponseModel[]> {
        return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetColumnsForArticleSelectionAsync}`);
    }

    getColumnsForDetailCapture(): Observable<ColumnResponseModel[]> {
        return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetColumnsForDetailCaptureAsync}`);
    }

    getColumnsForDetailConsultation(): Observable<ColumnResponseModel[]> {
        return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetColumnsForDetailConsultationAsync}`);
    }

    getColumnsForDetailSelection(): Observable<ColumnResponseModel[]> {
        return this._httpClient.get<ColumnResponseModel[]>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetColumnsForDetailSelectionAsync}`);
    }

    getCompositeArticleDetailByKeys(compositeArticleId: number, articleId: number): Observable<CompositeArticleDetailModel> {
        const params = new HttpParams().set('compositeArticleId', compositeArticleId).set('articleId', articleId);
        return this._httpClient.get<CompositeArticleDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetCompositeArticleDetailByKeysAsync}`,
            { params: params }
        );
    }

    getCompositeArticleDetailsForCapture(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], compositeArticleId: number): Observable<PagedResponseModel<CompositeArticleDetailCaptureTableModel>> {
        const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('compositeArticleId', compositeArticleId);
        return this._httpClient.post<PagedResponseModel<CompositeArticleDetailCaptureTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetCompositeArticleDetailsForCaptureAsync}`,
            filters, { params: params }
        );
    }

    getCompositeArticlesDetailsForSelection(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[], compositeArticleId: number): Observable<PagedResponseModel<CompositeArticleDetailSelectTableModel>> {
        const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant).set('compositeArticleId', compositeArticleId);
        return this._httpClient.post<PagedResponseModel<CompositeArticleDetailSelectTableModel>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetCompositeArticleDetailsForSelectionAsync}`,
            filters, { params: params }
        );
    }

    getCompositeArticleDetailsWithPagination(page: number, limit: number, columnSort: string | null, ascendant: boolean, filters: ColumnResponseModel[]): Observable<PagedResponseModel<any>> {
        const params = new HttpParams().set('page', page).set('limit', limit).set('columnSort', columnSort || '').set('ascendant', ascendant);
        return this._httpClient.post<PagedResponseModel<any>>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.GetCompositeArticleDetailsWithPaginationAsync}`,
            filters, { params: params }
        );
    }

    // importCompositeArticleDetails(compositeArticleDetailImports: Array<CompositeArticleDetailImportModel>): Observable<LoadPreInvoicingModel> {
    //     return this._httpClient.post<LoadPreInvoicingModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.ImportCompositeArticleDetailsAsync}`,
    //         compositeArticleDetailImports
    //     );
    // }

    update(compositeArticleDetailContract: CompositeArticleDetailModel): Observable<CompositeArticleDetailModel> {
        return this._httpClient.put<CompositeArticleDetailModel>(`${environment.apiCervinoBackend}${enpointCervinoWeb.compositeArticleDetail.UpdateAsync}`,
            compositeArticleDetailContract
        );
    }
}